/**
 * PNG decoding and encoding.
 *
 * The image pipeline needs a decoder that runs everywhere the core runs —
 * including a Web Worker and a Node test — so it cannot depend on a canvas.
 * PNG is the format worth implementing by hand: it is lossless, it is what
 * edited images are exported as, and its compression is plain DEFLATE, which
 * `fflate` already provides.
 *
 * JPEG is deliberately absent. A baseline JPEG encoder means DCT, quantisation
 * tables and Huffman coding, and getting any of that subtly wrong produces
 * images that look fine in one viewer and corrupt in another. The platform's
 * own encoder is used for JPEG output instead, and the catalog says so.
 */
// PNG's IDAT payload is a *zlib* stream — a two-byte header, DEFLATE data and
// an Adler-32 checksum — not raw DEFLATE. `fflate`'s `inflateSync`/`deflateSync`
// operate on raw DEFLATE, so using them here produces files that round-trip
// through this module and are rejected by every real PNG reader. `unzlibSync`
// and `zlibSync` are the wrapped forms.
import { unzlibSync, zlibSync } from 'fflate';
import { createImage } from '@moduviewer/core';
import type { RgbaImage } from '@moduviewer/core';
import { HandlerError } from '../contract.js';

const SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a] as const;

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]!) & 0xff]! ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

export interface PngInfo {
  readonly width: number;
  readonly height: number;
  readonly bitDepth: number;
  readonly colorType: number;
  readonly interlaced: boolean;
}

/* ------------------------------------------------------------------ *
 * decode
 * ------------------------------------------------------------------ */

interface Chunk { readonly type: string; readonly data: Uint8Array }

function readChunks(bytes: Uint8Array): Chunk[] {
  for (let i = 0; i < SIGNATURE.length; i++) {
    if (bytes[i] !== SIGNATURE[i]) throw new HandlerError('error.damagedOrIncomplete', 'not a PNG');
  }
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const chunks: Chunk[] = [];
  let pos = 8;

  while (pos + 8 <= bytes.length) {
    const length = view.getUint32(pos, false);
    const type = String.fromCharCode(
      bytes[pos + 4]!, bytes[pos + 5]!, bytes[pos + 6]!, bytes[pos + 7]!,
    );
    const dataStart = pos + 8;
    const dataEnd = dataStart + length;
    if (dataEnd + 4 > bytes.length) {
      throw new HandlerError('error.damagedOrIncomplete', `chunk ${type} runs past the end of the file`);
    }
    chunks.push({ type, data: bytes.subarray(dataStart, dataEnd) });
    if (type === 'IEND') break;
    pos = dataEnd + 4; // skip the CRC
  }
  return chunks;
}

const CHANNELS: Readonly<Record<number, number>> = { 0: 1, 2: 3, 3: 1, 4: 2, 6: 4 };

/** Undo the per-scanline filter. Filters are defined in terms of the *bytes* of
 * the previous pixel, which is why `bpp` is a byte distance rather than 1. */
function unfilter(
  raw: Uint8Array, width: number, height: number, bpp: number, bytesPerRow: number,
): Uint8Array {
  const out = new Uint8Array(height * bytesPerRow);
  let pos = 0;

  for (let y = 0; y < height; y++) {
    const filter = raw[pos++];
    if (filter === undefined) {
      throw new HandlerError('error.damagedOrIncomplete', `scanline ${y} is missing`);
    }
    const rowStart = y * bytesPerRow;
    const prevStart = rowStart - bytesPerRow;

    for (let x = 0; x < bytesPerRow; x++) {
      const value = raw[pos + x];
      if (value === undefined) {
        throw new HandlerError('error.damagedOrIncomplete', `scanline ${y} is truncated`);
      }
      const a = x >= bpp ? out[rowStart + x - bpp]! : 0;          // left
      const b = y > 0 ? out[prevStart + x]! : 0;                   // above
      const c = x >= bpp && y > 0 ? out[prevStart + x - bpp]! : 0; // above-left

      let recon: number;
      switch (filter) {
        case 0: recon = value; break;
        case 1: recon = value + a; break;
        case 2: recon = value + b; break;
        case 3: recon = value + ((a + b) >> 1); break;
        case 4: {
          // Paeth predictor: pick whichever neighbour the gradient points at.
          const p = a + b - c;
          const pa = Math.abs(p - a);
          const pb = Math.abs(p - b);
          const pc = Math.abs(p - c);
          recon = value + (pa <= pb && pa <= pc ? a : pb <= pc ? b : c);
          break;
        }
        default:
          throw new HandlerError('error.damagedOrIncomplete', `unknown PNG filter ${filter}`);
      }
      out[rowStart + x] = recon & 0xff;
    }
    pos += bytesPerRow;
  }
  return out;
}

/** Read a value of `bitDepth` bits from a packed row. */
function readSample(row: Uint8Array, index: number, bitDepth: number): number {
  if (bitDepth === 8) return row[index] ?? 0;
  if (bitDepth === 16) return row[index * 2] ?? 0; // high byte: 16 → 8 bit
  const perByte = 8 / bitDepth;
  const byte = row[Math.floor(index / perByte)] ?? 0;
  const shift = 8 - bitDepth * ((index % perByte) + 1);
  return (byte >> shift) & ((1 << bitDepth) - 1);
}

export function decodePng(bytes: Uint8Array): { image: RgbaImage; info: PngInfo } {
  const chunks = readChunks(bytes);
  const ihdr = chunks.find((c) => c.type === 'IHDR');
  if (!ihdr || ihdr.data.length < 13) {
    throw new HandlerError('error.damagedOrIncomplete', 'PNG has no IHDR chunk');
  }

  const hv = new DataView(ihdr.data.buffer, ihdr.data.byteOffset, ihdr.data.byteLength);
  const width = hv.getUint32(0, false);
  const height = hv.getUint32(4, false);
  const bitDepth = ihdr.data[8]!;
  const colorType = ihdr.data[9]!;
  const interlaced = ihdr.data[12] === 1;

  if (width === 0 || height === 0) {
    throw new HandlerError('error.damagedOrIncomplete', 'PNG declares zero dimensions');
  }
  if (interlaced) {
    // Adam7 is rare in practice and doubling the decoder's surface for it would
    // be poor value; saying so beats producing a scrambled image (§19).
    throw new HandlerError('error.featuresUnsupported', 'interlaced (Adam7) PNG is not supported');
  }
  const channels = CHANNELS[colorType];
  if (channels === undefined) {
    throw new HandlerError('error.featuresUnsupported', `unsupported PNG colour type ${colorType}`);
  }

  const idat = chunks.filter((c) => c.type === 'IDAT');
  if (idat.length === 0) {
    throw new HandlerError('error.damagedOrIncomplete', 'PNG has no image data');
  }
  const compressed = concat(idat.map((c) => c.data));

  let raw: Uint8Array;
  try {
    raw = unzlibSync(compressed);
  } catch (err) {
    throw new HandlerError('error.damagedOrIncomplete', `could not inflate PNG data: ${(err as Error).message}`);
  }

  const bitsPerPixel = channels * bitDepth;
  const bytesPerRow = Math.ceil((width * bitsPerPixel) / 8);
  const bpp = Math.max(1, Math.ceil(bitsPerPixel / 8));
  const unfiltered = unfilter(raw, width, height, bpp, bytesPerRow);

  // Palette and transparency, when present.
  const plte = chunks.find((c) => c.type === 'PLTE')?.data;
  const trns = chunks.find((c) => c.type === 'tRNS')?.data;
  if (colorType === 3 && !plte) {
    throw new HandlerError('error.damagedOrIncomplete', 'palette PNG has no PLTE chunk');
  }

  const image = createImage(width, height);
  const maxValue = (1 << bitDepth) - 1;
  const scale = bitDepth === 8 || bitDepth === 16 ? 1 : 255 / maxValue;

  for (let y = 0; y < height; y++) {
    const row = unfiltered.subarray(y * bytesPerRow, (y + 1) * bytesPerRow);
    for (let x = 0; x < width; x++) {
      let r = 0, g = 0, b = 0, a = 255;

      if (colorType === 0) {
        r = g = b = readSample(row, x, bitDepth) * scale;
      } else if (colorType === 2) {
        r = readSample(row, x * 3, bitDepth);
        g = readSample(row, x * 3 + 1, bitDepth);
        b = readSample(row, x * 3 + 2, bitDepth);
      } else if (colorType === 3) {
        const index = readSample(row, x, bitDepth);
        r = plte![index * 3] ?? 0;
        g = plte![index * 3 + 1] ?? 0;
        b = plte![index * 3 + 2] ?? 0;
        a = trns?.[index] ?? 255;
      } else if (colorType === 4) {
        r = g = b = readSample(row, x * 2, bitDepth) * scale;
        a = readSample(row, x * 2 + 1, bitDepth) * scale;
      } else {
        r = readSample(row, x * 4, bitDepth);
        g = readSample(row, x * 4 + 1, bitDepth);
        b = readSample(row, x * 4 + 2, bitDepth);
        a = readSample(row, x * 4 + 3, bitDepth);
      }

      const i = (y * width + x) * 4;
      image.data[i] = r; image.data[i + 1] = g; image.data[i + 2] = b; image.data[i + 3] = a;
    }
  }

  return { image, info: { width, height, bitDepth, colorType, interlaced } };
}

/* ------------------------------------------------------------------ *
 * encode
 * ------------------------------------------------------------------ */

function chunk(type: string, data: Uint8Array): Uint8Array {
  const out = new Uint8Array(12 + data.length);
  const view = new DataView(out.buffer);
  view.setUint32(0, data.length, false);
  for (let i = 0; i < 4; i++) out[4 + i] = type.charCodeAt(i);
  out.set(data, 8);
  view.setUint32(8 + data.length, crc32(out.subarray(4, 8 + data.length)), false);
  return out;
}

/**
 * Choose the filter that minimises the sum of absolute differences.
 *
 * This is the heuristic the PNG specification itself recommends. Filtering
 * before DEFLATE typically halves the output — writing every row unfiltered
 * would produce files two to three times larger for no benefit.
 */
function filterRow(
  row: Uint8Array, prev: Uint8Array | null, bpp: number,
): { filter: number; data: Uint8Array } {
  const candidates: { filter: number; data: Uint8Array; score: number }[] = [];
  const n = row.length;

  for (const filter of [0, 1, 2, 3, 4]) {
    const data = new Uint8Array(n);
    for (let x = 0; x < n; x++) {
      const raw = row[x]!;
      const a = x >= bpp ? row[x - bpp]! : 0;
      const b = prev ? prev[x]! : 0;
      const c = x >= bpp && prev ? prev[x - bpp]! : 0;

      let value: number;
      switch (filter) {
        case 0: value = raw; break;
        case 1: value = raw - a; break;
        case 2: value = raw - b; break;
        case 3: value = raw - ((a + b) >> 1); break;
        default: {
          const p = a + b - c;
          const pa = Math.abs(p - a);
          const pb = Math.abs(p - b);
          const pc = Math.abs(p - c);
          value = raw - (pa <= pb && pa <= pc ? a : pb <= pc ? b : c);
        }
      }
      data[x] = value & 0xff;
    }
    let score = 0;
    for (const v of data) score += v < 128 ? v : 256 - v;
    candidates.push({ filter, data, score });
  }

  candidates.sort((x, y) => x.score - y.score);
  const best = candidates[0]!;
  return { filter: best.filter, data: best.data };
}

export interface EncodePngOptions {
  /** 0–9. Higher is smaller and slower; 6 is the usual balance. */
  readonly level?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
  /** Drop the alpha channel when every pixel is opaque. Saves ~25%. */
  readonly stripOpaqueAlpha?: boolean;
}

function isFullyOpaque(image: RgbaImage): boolean {
  for (let i = 3; i < image.data.length; i += 4) {
    if (image.data[i] !== 255) return false;
  }
  return true;
}

export function encodePng(image: RgbaImage, options: EncodePngOptions = {}): Uint8Array {
  const { width, height } = image;
  const opaque = options.stripOpaqueAlpha !== false && isFullyOpaque(image);
  const channels = opaque ? 3 : 4;
  const colorType = opaque ? 2 : 6;
  const bytesPerRow = width * channels;

  const rawRows = new Uint8Array(height * (1 + bytesPerRow));
  let prev: Uint8Array | null = null;
  let pos = 0;

  for (let y = 0; y < height; y++) {
    const row = new Uint8Array(bytesPerRow);
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4;
      const o = x * channels;
      row[o] = image.data[i]!;
      row[o + 1] = image.data[i + 1]!;
      row[o + 2] = image.data[i + 2]!;
      if (!opaque) row[o + 3] = image.data[i + 3]!;
    }
    const { filter, data } = filterRow(row, prev, channels);
    rawRows[pos++] = filter;
    rawRows.set(data, pos);
    pos += bytesPerRow;
    prev = row;
  }

  const ihdr = new Uint8Array(13);
  const hv = new DataView(ihdr.buffer);
  hv.setUint32(0, width, false);
  hv.setUint32(4, height, false);
  ihdr[8] = 8;            // bit depth
  ihdr[9] = colorType;
  ihdr[10] = 0;           // deflate
  ihdr[11] = 0;           // adaptive filtering
  ihdr[12] = 0;           // no interlace

  const idat = zlibSync(rawRows, { level: options.level ?? 6 });

  return concat([
    Uint8Array.from(SIGNATURE),
    chunk('IHDR', ihdr),
    chunk('IDAT', idat),
    chunk('IEND', new Uint8Array(0)),
  ]);
}

function concat(parts: readonly Uint8Array[]): Uint8Array {
  const total = parts.reduce((n, p) => n + p.length, 0);
  const out = new Uint8Array(total);
  let pos = 0;
  for (const part of parts) { out.set(part, pos); pos += part.length; }
  return out;
}

/** Cheap check before committing to a full decode. */
export function isPng(bytes: Uint8Array): boolean {
  if (bytes.length < SIGNATURE.length) return false;
  for (let i = 0; i < SIGNATURE.length; i++) if (bytes[i] !== SIGNATURE[i]) return false;
  return true;
}
